import { useEffect, useState } from "react";
import { backfillMissingEv, getAllHandsChronological, type StoredHand } from "../history/db.js";
import { computeWinLossSummary } from "../history/summary.js";
import { HandDetailModalLoader } from "../components/HandDetailModalLoader.js";
import { CardView } from "../components/CardView.js";
import { PnlGraph } from "../components/PnlGraph.js";
import { chipsToBb } from "../engine/selectors.js";
import { useGameStore } from "../store/gameStore.js";

const NAMES: readonly [string, string] = ["あなた", "Bot"];
const LIST_LIMIT = 100;

export function HistoryScreen() {
  // Full history, oldest first, across all matches -- the PnL graph needs
  // the whole thing to compute a cumulative baseline that carries over
  // between matches instead of resetting to 0 (docs/UI_REFERENCE.md
  // "フィードバック反映"). The list/summary badge only look at the most
  // recent LIST_LIMIT of these.
  const [allChronological, setAllChronological] = useState<StoredHand[] | null>(null);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const goPlay = useGameStore((s) => s.goPlay);
  const goStart = useGameStore((s) => s.goStart);
  const hasMatch = useGameStore((s) => s.hand !== null);

  useEffect(() => {
    let cancelled = false;
    void getAllHandsChronological().then((loaded) => {
      if (cancelled) return;
      setAllChronological(loaded);
      // Backfill evNetBb for rows saved before that field existed, then
      // swap in the updated set once done (UI already usable meanwhile).
      void backfillMissingEv(loaded).then((filled) => {
        if (!cancelled) setAllChronological(filled);
      });
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Newest-first, capped, for the list + summary badge (mirrors the old
  // getRecentHands(100) contract).
  const hands = allChronological ? allChronological.slice(-LIST_LIMIT).reverse() : null;

  const summary = hands ? computeWinLossSummary(hands) : null;

  return (
    <div className="screen history-screen">
      <div className="history-header">
        <div className="history-header-title">
          <h2>ハンド履歴</h2>
          {summary && (summary.wins > 0 || summary.losses > 0) && (
            <span className="history-summary-badge">
              {summary.wins}勝{summary.losses}敗
              {summary.streak >= 1 &&
                `・${summary.streak}連${summary.streakType === "win" ? "勝" : "敗"}中`}
            </span>
          )}
        </div>
        <button type="button" className="secondary-btn" onClick={hasMatch ? goPlay : goStart}>
          {hasMatch ? "プレイに戻る" : "スタートへ"}
        </button>
      </div>

      {hands === null && <p>読み込み中…</p>}
      {hands !== null && hands.length === 0 && <p className="settings-note">まだハンドがありません。</p>}
      {allChronological !== null && allChronological.length > 0 && (
        <PnlGraph hands={allChronological} windowSize={LIST_LIMIT} />
      )}

      <div className="history-list">
        {hands?.map((h) => {
          const board = h.record.board;
          return (
            <button key={h.id} type="button" className="history-item" onClick={() => setSelectedId(h.id)}>
              <div className="history-item-top">
                <span className="history-item-time">{new Date(h.timestamp).toLocaleString("ja-JP")}</span>
                <span className="position-badge">{h.record.positions[0]}</span>
                <span className={`hist-net ${h.heroNetChips >= 0 ? "result-win" : "result-loss"}`}>
                  {h.heroNetChips >= 0 ? "+" : ""}
                  {chipsToBb(h.heroNetChips).toFixed(1)}bb
                </span>
              </div>
              <div className="history-item-cards">
                <CardView card={h.record.holeCards[0][0]} size="sm" />
                <CardView card={h.record.holeCards[0][1]} size="sm" />
                {board.jokerRevealed && <CardView joker size="sm" />}
                {board.flopReal && (
                  <>
                    <CardView card={board.flopReal[0]} size="sm" />
                    <CardView card={board.flopReal[1]} size="sm" />
                  </>
                )}
                {board.turn !== undefined && <CardView card={board.turn} size="sm" />}
                {board.river !== undefined && <CardView card={board.river} size="sm" />}
              </div>
            </button>
          );
        })}
      </div>

      {selectedId !== null && (
        <HandDetailModalLoader id={selectedId} names={NAMES} onClose={() => setSelectedId(null)} />
      )}
    </div>
  );
}
